import { useNavigate } from '@tanstack/react-router';
import { CalendarPlus, Gavel } from 'lucide-react';
import type { ReactNode } from 'react';
import { UNBOOKED_INTERVIEW_STATUSES } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useApplicationInterviews } from '../api/interviews-api';
import { useViewerZone } from '../hooks/use-interview-actions';
import { InterviewRow } from './InterviewRow';
import styles from './Interviews.module.css';

/**
 * CAND-03's interviews — each round's, newest first, with "Schedule" on any still waiting
 * for a time. Created by the system as the applicant reaches each interview stage (F10.01),
 * so there is nothing to add by hand.
 */
export function ApplicationInterviewsCard({ applicationId }: { readonly applicationId: string }): ReactNode {
  const canView = useCanView('interview');
  const canEditInterviews = useCanEdit('interview');
  const canConfigureJobs = useCanView('job_config');
  const canSeeDecisions = useCanView('stage_decision');
  const canSchedule = canEditInterviews && canConfigureJobs;
  const zone = useViewerZone();
  const navigate = useNavigate();
  const query = useApplicationInterviews(applicationId, canView);
  if (!canView) return null;
  if (query.isPending) {
    return (
      <Card title="Interviews">
        <SkeletonLines count={2} />
      </Card>
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) return null;
  return (
    <Card title="Interviews">
      <ul className={styles.list}>
        {rows.map((row) => (
          <InterviewRow
            key={row.id}
            interview={row}
            zone={zone}
            isJobShown={false}
            action={
              canSchedule && UNBOOKED_INTERVIEW_STATUSES.includes(row.status) ? (
                <Button size="sm" variant="solid" icon={CalendarPlus} onClick={() => void navigate({ to: '/interviews/$interviewId/schedule', params: { interviewId: row.id } })}>
                  Schedule
                </Button>
              ) : canSeeDecisions && (row.status === 'completed' || row.status === 'panelist_no_show') ? (
                <Button size="sm" variant="outline" icon={Gavel} onClick={() => void navigate({ to: '/interviews/$interviewId/debrief', params: { interviewId: row.id } })}>
                  Debrief
                </Button>
              ) : undefined
            }
          />
        ))}
      </ul>
    </Card>
  );
}
